import { useState, useCallback } from 'react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import api from './api';

/**
 * Shared hook for owner-authed actions with inline unlock recovery.
 *
 * Usage:
 *   const { ownerFetch, unlockProps } = useOwnerFetch();
 *   const result = await ownerFetch(() => api.post('/owner/foo', body));
 *   // result = { ok: true, data } | { ok: false, error: 'human string' }
 *   // render <OwnerUnlockPrompt {...unlockProps} /> near your save button
 */
export function useOwnerFetch() {
  const [ownerExpired, setOwnerExpired] = useState(false);
  const [unlockPassword, setUnlockPassword] = useState('');
  const [unlockError, setUnlockError] = useState('');
  const [unlocking, setUnlocking] = useState(false);
  const [pendingRetry, setPendingRetry] = useState(null);

  const ownerFetch = useCallback(async (requestFn) => {
    try {
      const resp = await requestFn();
      setOwnerExpired(false);
      return { ok: true, data: resp.data };
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if ((status === 401 || status === 403) && detail === 'owner_required') {
        setOwnerExpired(true);
        setUnlockError('');
        setPendingRetry(() => requestFn);
        return { ok: false, error: null }; // null = unlock prompt handles it
      }
      const msg = detail
        ? (typeof detail === 'string' ? detail : JSON.stringify(detail))
        : 'Something went wrong. Please try again.';
      return { ok: false, error: msg };
    }
  }, []);

  const handleUnlock = useCallback(async () => {
    if (!unlockPassword.trim()) return null;
    setUnlocking(true);
    setUnlockError('');
    try {
      const { data } = await api.post('/auth/verify-owner', { password: unlockPassword.trim() });
      const token = data.token || data;
      if (token && typeof token === 'string') {
        sessionStorage.setItem('foretour_platform_token', token);
      }
      setOwnerExpired(false);
      setUnlockPassword('');
      // Auto-retry the pending request
      if (pendingRetry) {
        const retryFn = pendingRetry;
        setPendingRetry(null);
        try {
          const resp = await retryFn();
          return { ok: true, data: resp.data };
        } catch (err2) {
          const detail2 = err2?.response?.data?.detail;
          return { ok: false, error: detail2 ? String(detail2) : 'Something went wrong. Please try again.' };
        }
      }
      return { ok: true, data: null };
    } catch {
      setUnlockError('Incorrect password');
      return null;
    } finally { setUnlocking(false); }
  }, [unlockPassword, pendingRetry]);

  return {
    ownerFetch,
    unlockProps: {
      ownerExpired, unlockPassword, setUnlockPassword, unlockError, unlocking, handleUnlock,
    },
  };
}

/**
 * Inline owner unlock prompt — renders the yellow card.
 * Place this near the save/action button on any owner screen.
 */
export function OwnerUnlockPrompt({ ownerExpired, unlockPassword, setUnlockPassword, unlockError, unlocking, handleUnlock }) {
  if (!ownerExpired) return null;
  return (
    <Card className="p-4 bg-[#FFF8E1] border-[#FFE082] rounded-xl space-y-3" data-testid="owner-unlock-section">
      <p className="text-sm text-[#1D242B] font-semibold">Owner unlock expired. Enter the owner password to continue.</p>
      <div className="flex gap-2">
        <Input type="password" value={unlockPassword} onChange={e => { setUnlockPassword(e.target.value); }}
          placeholder="Your Fore Tour owner password" className="h-10 rounded-lg text-sm flex-1" autoComplete="off"
          data-testid="owner-unlock-password"
          onKeyDown={e => { if (e.key === 'Enter') handleUnlock(); }} />
        <Button onClick={handleUnlock} disabled={unlocking || !unlockPassword.trim()}
          className="h-10 px-4 bg-[#1B4332] text-white rounded-lg text-sm font-semibold" data-testid="owner-unlock-btn">
          {unlocking ? '...' : 'Unlock'}
        </Button>
      </div>
      {unlockError && <p className="text-xs text-[#D32F2F]" data-testid="owner-unlock-error">{unlockError}</p>}
    </Card>
  );
}
